import {
  Button,
  EllipsisVerticalIcon,
  Grid,
  MenuButton,
  Switch as ToggleSwitch,
  useInterstateToken
} from "@dealer-center/core-components";
import styled from "styled-components";
import { SaveModal } from "./save";
import type { resourceRefsInterface } from ".";
import type { resources } from "@dealer-center/ddc-pages-resources-interface";
import { CompareTool } from "./compare";
import { useWindowDimensions } from "@dealer-center/core-custom-components";
import { useCC9Flags } from "@dealer-center/core-third-party-launch-darkly";
import { useNavigate } from "react-router-dom";
interface HeaderAreaInterface {
  handleShowOnlyConfigurableToggled: () => void;
  handleShowOnlySelectedToggled: () => void;
  currentResources: resources[];
  resetClicked: () => void;
  resourceRefs: resourceRefsInterface;
  firstLetters: string[];
  existingResources: resources[];
  showOnlyConfigurableToggle: boolean;
  showOnlyCheckedToggle: boolean;
  parentAccounts: string[];
  resetCheckBoxesToMatchAccount: (account: string) => void;
  dealerId: string;
}
const StyledLetterLinkArea = styled.ul`
  display: flex;
  flex-wrap: wrap;
  padding-left: 0;
  list-style: none;
  margin-left: -3px;
  clip-path: inset(0 0 0 3px);
  margin-top: 0;
  margin-bottom: 0;
`;
const StyledLetterLink = styled.li`
  padding-left: 1px;
  &::before {
    content: "";
    margin-right: 1px;
    border-left: solid 1px #6d6d6d;
  }
`;
const StyledLetterLinkButton = styled(Button)``;
const showAllLetters = false;
const StyledHeaderArea = styled.div<{ borderColor: string }>`
  margin-bottom: 1em;
  padding-bottom: 1em;
  border-bottom: 1px solid ${props => props.borderColor};
`;

export function HeaderArea({
  handleShowOnlyConfigurableToggled,
  handleShowOnlySelectedToggled,
  currentResources,
  resetClicked,
  firstLetters,
  resourceRefs,
  existingResources,
  showOnlyConfigurableToggle,
  showOnlyCheckedToggle,
  parentAccounts,
  resetCheckBoxesToMatchAccount,
  dealerId
}: HeaderAreaInterface) {
  const navigate = useNavigate();
  const { "cc9.resources-hide-ui": hideInheritFromParent } = useCC9Flags();
  const { isSmOrGreater } = useWindowDimensions();
  const getToken = useInterstateToken();
  const borderColor = getToken("sem.color.border.default") || "#6d6d6d";

  const handleAuditHistoryButton = () => {
    navigate(`/dealer/${dealerId}/resourceAutoLog`);
  };

  return (
    <StyledHeaderArea borderColor={borderColor}>
      <StyledLetterLinkArea>
        {showAllLetters
          ? "abcdefghijklmnopqrstuvwxyz"
          : firstLetters
              .join("")
              .toUpperCase()
              .split("")
              .map(key => (
                <StyledLetterLink key={key}>
                  <StyledLetterLinkButton
                    analyticsConfiguration={{
                      eventName: "Letter Link Clicked"
                    }}
                    buttonStyle="aux"
                    disabled={!firstLetters.includes(key)}
                    sx={{
                      width: "1.5em",
                      fontSize: "1.2em",
                      padding: "0 0.5em",
                      fontWeight: "600"
                    }}
                    onClick={() => {
                      const resource = resourceRefs[key];
                      if (!resource?.ref.current) return;
                      resource.ref.current.scrollIntoView({
                        behavior: "smooth"
                      });
                    }}
                  >
                    {key}
                  </StyledLetterLinkButton>
                </StyledLetterLink>
              ))}
      </StyledLetterLinkArea>
      <Grid
        container
        spacing={isSmOrGreater ? 2 : 1}
        sx={{ marginTop: "4px", marginBottom: "4px" }}
      >
        <Grid>
          <ToggleSwitch
            checked={showOnlyConfigurableToggle}
            label="Show only Configurable"
            layout="vertical"
            onClick={handleShowOnlyConfigurableToggled}
          />
        </Grid>
        <Grid>
          <ToggleSwitch
            checked={showOnlyCheckedToggle}
            label="Show only Selected"
            layout="vertical"
            onClick={handleShowOnlySelectedToggled}
          />
        </Grid>
      </Grid>
      <Grid container spacing={isSmOrGreater ? 2 : 1}>
        <CompareTool />
        {parentAccounts.length > 0 && !hideInheritFromParent && (
          <Grid>
            <MenuButton
              ButtonProps={{
                buttonStyle: "secondary",
                endIcon: <EllipsisVerticalIcon title="Inherit from parent" />
              }}
              MenuProps={{
                menuOptions: parentAccounts.map(account => {
                  return {
                    label: account,
                    value: account,
                    onSelect: () => {
                      resetCheckBoxesToMatchAccount(account);
                    }
                  };
                })
              }}
            >
              Inherit from parent
            </MenuButton>
          </Grid>
        )}
        <Grid>
          <Button
            analyticsConfiguration={{
              eventName: "Account resources - Go to Audit History Page"
            }}
            buttonStyle="secondary"
            onClick={handleAuditHistoryButton}
          >
            Audit History
          </Button>
        </Grid>
        <Grid marginLeft={"auto"}>
          <SaveModal
            currentResources={currentResources}
            existingResources={existingResources}
            resetClicked={resetClicked}
          />
        </Grid>
      </Grid>
    </StyledHeaderArea>
  );
}

export default HeaderArea;
